"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-aluminum-extrusion";
const IMAGE_BASE = "/image/new-version/more/AluminumExtrusion/materials-for-custom-aluminum-extrusion";

const MATERIALS = [
	{
		keyPrefix: "materialsAlloy6061",
		image: `${IMAGE_BASE}/alloy-6061.webp`,
	},
	{
		keyPrefix: "materialsAlloy6063",
		image: `${IMAGE_BASE}/alloy-6063.webp`,
	},
	{
		keyPrefix: "materialsAlloy1100",
		image: `${IMAGE_BASE}/alloy-1100.webp`,
	},
] as const;

type MaterialKey = (typeof MATERIALS)[number]["keyPrefix"];

export default function AluminumExtrusionMaterials() {
	const t = useTranslations(NAMESPACE);
	const [activeKey, setActiveKey] = useState<MaterialKey>("materialsAlloy6061");
	const activeMaterial = MATERIALS.find((material) => material.keyPrefix === activeKey) ?? MATERIALS[0];

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("materialsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">
						{t("materialsDescBefore")}
						<strong className="font-bold">{t("materialsDescBold")}</strong>
						{t("materialsDescAfter")}
					</p>
				</header>

				<div>
					<h3 className="mb-3 text-lg font-bold text-black sm:text-xl">{t("materialsGroupTitle")}</h3>
					<div className="mb-6 h-1 w-full bg-themePrimary400" />

					<div className="grid gap-6 lg:grid-cols-[0.9fr_1.45fr_1.55fr] lg:items-start lg:gap-10">
						<div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
							{MATERIALS.map((material) => {
								const isActive = material.keyPrefix === activeKey;
								return (
									<button
										key={material.keyPrefix}
										type="button"
										onClick={() => setActiveKey(material.keyPrefix)}
										className={`min-w-[170px] px-4 py-3 text-left text-sm font-semibold transition-all duration-300 lg:w-full ${
											isActive
												? "bg-themePrimary400 text-white"
												: "bg-transparent text-black hover:bg-white hover:text-themePrimary400"
										}`}
										aria-pressed={isActive}
									>
										{t(`${material.keyPrefix}Title`)}
									</button>
								);
							})}
						</div>

						<div className="relative aspect-[4/3] w-full overflow-hidden bg-white">
							<Image
								src={activeMaterial.image}
								alt={t(`${activeMaterial.keyPrefix}ImageAlt`)}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 34vw"
							/>
						</div>

						<div className="pt-1 lg:pt-0">
							<h3 className="mb-4 text-xl font-bold text-black sm:text-2xl">
								{t(`${activeMaterial.keyPrefix}Title`)}
							</h3>
							<p className="text-sm leading-relaxed text-black md:text-base">
								{t(`${activeMaterial.keyPrefix}Desc`)}
							</p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
